import React, { useState } from "react";
import {
  makeStyles,
  Typography,
  IconButton,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { changePassword } from "../apis";
import { changePasswordSchema } from "../utils/validations";
import Button from "./Button";
import useToken from "../hooks/useToken";
import { commonStrings, forgotPasswordStrings } from "./StringBundle";
import { useGlobalStyles } from "./globalStyles";
import { colors } from "../constants/colors";
import { fontSize, fontWeight, lineHeight } from "../constants/typography";
import { useForm, useWatch, FormProvider } from "react-hook-form";
import TextFieldInput from "./TextFieldInput";

const useStyles = makeStyles((theme) => ({
  paper: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: 460,
    backgroundColor: theme.palette.background.paper,
    borderRadius: 8,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(4),
    outline: "none",
    boxSizing: "border-box",
  },
  header: {
    marginBottom: theme.spacing(1),
  },
  closeButton: {
    position: "absolute",
    top: theme.spacing(1.5),
    right: theme.spacing(1.5),
    color: theme.palette.text.secondary,
  },
  title: {
    fontWeight: fontWeight.bold,
    fontSize: `${fontSize.icon} !important`,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1),
  },
  subtitle: {
    fontSize: `${fontSize.dataLabel} !important`,
    lineHeight: lineHeight.normal,
    color: theme.palette.text.secondary,
  },
  fieldRow: {
    marginBottom: theme.spacing(1),
  },
  btnWrapper: {
    marginTop: theme.spacing(2),
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(1),
    width: "100%",
    "& .ccl-btn": {
      width: "100%",
      margin: 0,
    },
  },
}));

export const ChangePass = React.memo(({ setSnack, setChangePass, forceChange = false }) => {
  const classes = useStyles();
  const globalClasses = useGlobalStyles();
  const { getCurrentUserDetails, getPreference } = useToken();
  let currentUserDetails = getCurrentUserDetails();
  const [loading, setLoading] = useState(false);
  const methods = useForm({ defaultValues: { oldPass: "", newPass: "" } });
  const { control, getValues, setError, formState } = methods;
  const newPass = useWatch({ control, name: "newPass" });
  const newPassMeetsRules = !changePasswordSchema.validate({ password: newPass }).error;
  // Same live hint as Forgot Password: shown until the new password meets the rules, unless an error is showing.
  const showPasswordHint = !formState.errors.newPass && !newPassMeetsRules;

  const onChangePass = async (event) => {
    event.preventDefault();
    const { oldPass, newPass } = getValues();
    const value = changePasswordSchema.validate({ password: newPass });
    if (value?.error?.details.length) {
      if (value.error.details[0].path.includes("password")) {
        setError("newPass", {
          type: "manual",
          message:commonStrings.passwordRequirements
        });
      }
    } else if (!oldPass) {
      setError("oldPass", { type: "manual", message: commonStrings.errormessage });
    } else {
      setLoading(true);
      try {
        const response = await changePassword({
          user_id: currentUserDetails.id,
          email_id: getPreference()?.name,
          previouspassword: oldPass,
          newpassword: newPass,
          last_password_modified_date: new Date(),
        });
        if (response) {
          setChangePass(false);
          setSnack({
            open: true,
            message: commonStrings.passwordUpdatedSuccess,
            severity: commonStrings.success,
          });
          let value = localStorage.getItem("preference");
          const preference = JSON.parse(value);
          preference.lastPasswordDate = new Date();
          localStorage.setItem("preference", JSON.stringify(preference));
          setTimeout(() => {
            setSnack({
              open: false,
              message: "",
            });
          }, 2000);
        }
      } catch (e) {
        const serverMessage = e?.response?.data?.error || e?.response?.data?.errors?.join(", ");
        setSnack({
          open: true,
          message: serverMessage || commonStrings.serverError,
          severity: commonStrings.error,
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const onCancel = () => {
    setChangePass(false);
  };

  return (
    <FormProvider {...methods}>
      <div className={classes.paper}>

        {!forceChange && (
          <IconButton
            size="small"
            className={classes.closeButton}
            aria-label="Close"
            onClick={() => setChangePass(false)}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}

        {/* Title + Subtitle */}
        <div className={classes.header}>
          <Typography className={classes.title}>
            {commonStrings.changePasswordTitle}
          </Typography>
          <Typography className={classes.subtitle}>
            {commonStrings.changePasswordSubtitle}
          </Typography>
        </div>

        {/* Old Password */}
        <div className={classes.fieldRow}>
          <TextFieldInput
            name="oldPass"
            control={control}
            label={commonStrings.oldPasswordLabel}
            required
            inputType="password"
          />
        </div>

        {/* New Password */}
        <div className={classes.fieldRow}>
          <TextFieldInput
            name="newPass"
            control={control}
            label={commonStrings.newPasswordLabel}
            required
            inputType="password"
          />
          {showPasswordHint && (
            <span className={globalClasses.loginHintText}>{forgotPasswordStrings.passwordHint}</span>
          )}
        </div>

        {/* Change Password Button — full width */}
        <div className={classes.btnWrapper}>
          <Button
            fullWidth
            disabled={loading || !newPassMeetsRules}
            label={loading ? commonStrings.changePasswordLoading : commonStrings.changePasswordBtn}
            onClick={onChangePass}
          />
          {!forceChange && (
            <Button
              fullWidth
              label={commonStrings.cancel}
              onClick={onCancel}
            />
          )}
        </div>
      </div>
    </FormProvider>
  );
});
